import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";


import { getTemplates, deleteTemplate } from "@/services/templateServices/templateAction";
import NotificationAlertOptions from "@/layouts/Alerts";
import getErrorMessage, { hasDisplayableError } from "@/utils/getErrorMessage";
import DisplayTemplatesView from "./DisplayTemplatesView";
import type { TemplatesResponse } from "@/../../shared/apiTypes";
import { asCaught } from "@/services/caughtError";

type DisplayTemplatesContainerProps = {
  // The lean template row; IndexTemplate.tsx (SCRUM-2042) declares the same shape.
  handleUpdate?: (template: { _id?: string; name?: string }) => void;
};

const DisplayTemplatesContainer = ({ handleUpdate }: DisplayTemplatesContainerProps) => {
  const dispatch = useAppDispatch();

  // Redux
  const templatesStore = (useAppSelector((s) => s.templates?.templates || []) as any);
  const permission = useCurrentPermission();

  // UI state
  const [templates, setTemplates] = useState<TemplatesResponse>([]);
  const [message, setMessage] = useState("loading...");

  const [notificationModal, setNotificationModal] = useState(false);
  const [alert, setAlert] = useState<React.ReactNode>(null);

  // init fetch
  useEffect(() => {
    dispatch(getTemplates());
  }, [dispatch]);

  // sync from store
  useEffect(() => {
    if (!templatesStore) return;
    const list = (templatesStore || []).slice().reverse();
    setTemplates(list);
    setMessage(list.length === 0 ? "No available templates yet" : "");
  }, [templatesStore]);

  const toggleModal = useCallback(() => setNotificationModal((v) => !v), []);

  const notify = useCallback((type) => {
    NotificationAlertOptions(type, "Template Deleted Successfully");
  }, []);

  const hideAlert = useCallback(() => setAlert(null), []);

  const basicAlert = useCallback((e, var_types, template_name, varArray) => {
    e?.preventDefault?.();
    const title = `${template_name} template ${var_types} : ${varArray.length}`;
    setAlert(
     
      <div style={{ zIndex: 1100 }}>
        <div style={{ display: "block", marginTop: "-100px" }}>
          <div className="swal2-container swal2-center swal2-backdrop-show">
            <div className="swal2-popup" style={{ display: "grid" }}>
              <h2 className="swal2-title">{title}</h2>
              <div className="swal2-html-container" style={{ textAlign: "left" }}>
                {varArray.map((variable) => (
                  <React.Fragment key={variable.variable}>
                    <strong>
                      {variable.type === "Array"
                        ? `${variable.variable} (${variable.type} of ${variable.complextype})`
                        : `${variable.variable} (${variable.type})`}
                    </strong>
                    <br />
                  </React.Fragment>
                ))}
              </div>
              <div className="swal2-actions">
                <button className="swal2-confirm swal2-styled" onClick={hideAlert}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [hideAlert]);

  const questionAlert = useCallback(
    (e, template) => {
      e.preventDefault();
      setAlert(
        <div style={{ zIndex: 1100 }}>
          <div style={{ display: "block", marginTop: "-100px" }}>
            <div className="swal2-container swal2-center swal2-backdrop-show">
              <div className="swal2-popup" style={{ display: "grid" }}>
                <div
                  className="swal2-icon swal2-question swal2-animate-question-icon text-warning"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <span className="swal2-icon-text">?</span>
                </div>
                <h2 className="swal2-title">Delete Template Operation</h2>
                <div className="swal2-html-container">Are you sure to delete {template.name}</div>
                <div className="swal2-actions">
                  <button
                    className="swal2-confirm swal2-styled"
                    onClick={async () => {
                      hideAlert();
                      try {
                        await dispatch(deleteTemplate(template._id));
                        notify("success");
                      } catch (err) {
                        // The SCRUM-1473 guard refuses a still-referenced template with
                        // a 409 whose reason sits in `errors[0].msg` (the repo's error
                        // contract), not in `message`. Read the body through the shared
                        // normalizer so that reason reaches the editor; a body with no
                        // readable shape keeps the delete-specific fallback (SCRUM-1551).
                        const body = asCaught(err)?.response?.data;
                        NotificationAlertOptions(
                          "error",
                          hasDisplayableError(body)
                            ? getErrorMessage(body)
                            : "Deleting the template failed. Please try again."
                        );
                      }
                    }}
                  >
                    Yes I&apos;m sure
                  </button>
                  <button className="swal2-cancel swal2-styled" onClick={hideAlert}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    },
    [dispatch, hideAlert, notify]
  );

  const handleUpdateTemplate = useCallback(
    (elem) => (e) => {
      e?.preventDefault?.();
      handleUpdate?.(elem);
    },
    [handleUpdate]
  );

  /**
   * SCRUM-1128: search, sort and pagination now come from TableWrapper, so the
   * container only describes the columns. The two content columns and Tools
   * render buttons rather than a field, so they carry an explicit `id` — a
   * column without `accessorKey` has no id of its own.
   */
  const columns = useMemo(() => {
    const contentCountCell = (kind) => {
      const Cell = ({ row }) => {
        const element = row.original;
        const variables = element?.content_variables?.[kind.key];
        if (!(variables?.length > 0)) return null;
        return (
          <Button
            className="btn-icon-only rounded-circle"
            color="facebook"
            onClick={(e) => basicAlert(e, kind.label, element.name, variables)}
          >
            {variables.length}
          </Button>
        );
      };
      // react/display-name: a component built by a factory has no inferable name.
      Cell.displayName = `ContentCountCell(${kind.key})`;
      return Cell;
    };

    const base = [
      { accessorKey: "name", header: "Name" },
      { accessorKey: "type", header: "type" },
      { accessorKey: "template_src", header: "Source Path" },
      { accessorKey: "design.name", header: "Design Name" },
      {
        id: "contentRequired",
        header: "Required Content",
        cell: contentCountCell({ key: "ContentRequired", label: "Content Required" }),
      },
      {
        id: "contentOptional",
        header: "Optional Content",
        cell: contentCountCell({ key: "ContentOptional", label: "Content Optional" }),
      },
    ];

    if (!(permission?.publish || permission?.edit)) return base;

    return [
      ...base,
      {
        id: "tools",
        header: "Tools",
        cell: ({ row }) => {
          const element = row.original;
          return (
            <div className="table-actions">
              <Button
                className="mr-3"
                size="sm"
                onClick={handleUpdateTemplate(element)}
                id={`tooltip1${element._id}`}
              >
                <i className="ni ni-settings-gear-65" />
              </Button>
              <Button
                className="mr-4"
                color="danger"
                size="sm"
                onClick={(e) => questionAlert(e, element)}
              >
                <i className="fas fa-trash" />
              </Button>
            </div>
          );
        },
      },
    ];
  }, [permission, basicAlert, questionAlert, handleUpdateTemplate]);

  const ViewModel = useMemo(
    () => ({
      templates,
      columns,
      message,
      alert,
      notificationModal,
      toggleModal,
    }),
    [templates, columns, message, alert, notificationModal, toggleModal]
  );

  return <DisplayTemplatesView {...ViewModel} />;
};

export default DisplayTemplatesContainer;
